import { type ReactNode, useEffect, useState } from 'react'

import { useTailgateGrid } from '../hooks/useTailgateGrid'
import { useLightMode } from '@/hooks/useLightMode'
import { usePermissions } from '@/hooks/usePermissions'

import { Box, Flex, Icon, Tooltip } from '@chakra-ui/react'

import {
  Box as DarkBox,
  Button as DarkButton,
  Flex as DarkFlex,
  Tooltip as DarkTooltip,
  tokenPrefix,
  tokenVar,
} from '@hivewatch/dark'
import type { ColorScheme } from '@hivewatch/dark'

import { AssociatedDevices } from '@/components/icons'
import { Button } from '@/components/ui'
import { BUTTON_PRESS } from '@/utils/analytics'

import { GridToolTip } from './GridToolTip'

const TAILGATE_GRID_AUTO_REFRESH_INTERVAL_MS = 3000

interface GridModalDetailsIProps {
  colorScheme?: ColorScheme
}

const DarkTooltipTarget = ({
  children,
  colorScheme,
  label,
}: {
  children: ReactNode
  colorScheme: ColorScheme
  label: string
}) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)

  return (
    <DarkBox
      onMouseEnter={(event) => setAnchorEl(event.currentTarget)}
      onMouseLeave={() => setAnchorEl(null)}
    >
      {children}
      <DarkTooltip
        anchorEl={anchorEl}
        colorScheme={colorScheme}
        label={label}
        variant='description'
      />
    </DarkBox>
  )
}

export const GridModalDetails = ({ colorScheme }: GridModalDetailsIProps) => {
  const mode = useLightMode()
  const tokenPathPrefix = colorScheme ? tokenPrefix(colorScheme) : undefined
  const primaryTextColor = colorScheme
    ? tokenVar(`colors.${tokenPathPrefix}text.primary`)
    : `text.primary.${mode}`
  const secondaryTextColor = colorScheme
    ? tokenVar(`colors.${tokenPathPrefix}text.secondary`)
    : `text.secondary.${mode}`
  const { shouldEnableConfigureTailgateGrid } = usePermissions()

  const {
    clearBoxes,
    setInitialBoxes,
    refreshSnapshot,
    isRefreshingImage,
    isTailgateGridOpen,
    isGridConfigured,
    associateDeviceValues,
    trackTailgateEvent,
  } = useTailgateGrid()

  useEffect(() => {
    if (!isTailgateGridOpen) {
      return
    }

    // Auto-refresh the camera frame every 3s while the modal is open.
    // This reuses the same refresh action as the manual button without
    // adding tracking noise, and cleanup stops refresh immediately on close.
    const intervalId = window.setInterval(() => {
      if (!isRefreshingImage) {
        refreshSnapshot()
      }
    }, TAILGATE_GRID_AUTO_REFRESH_INTERVAL_MS)

    return () => {
      window.clearInterval(intervalId)
    }
  }, [isTailgateGridOpen, isRefreshingImage, refreshSnapshot])

  const onRefreshCamera = () => {
    trackTailgateEvent(`${BUTTON_PRESS} Tailgate Editor - Refresh Camera`)
    refreshSnapshot()
  }
  const onRevertChanges = () => {
    trackTailgateEvent(`${BUTTON_PRESS} Tailgate Editor - Revert Changes`)
    setInitialBoxes()
  }
  const onClearGrid = () => {
    trackTailgateEvent(`${BUTTON_PRESS} Tailgate Editor - Clear Grids`)
    clearBoxes()
  }

  if (colorScheme) {
    return (
      <DarkFlex direction='column' mb='20px'>
        <DarkFlex alignItems='center' direction='row'>
          <AssociatedDevices strokeColor='#4DBD6B' />
          <DarkBox
            color={primaryTextColor}
            fontSize='12px'
            fontWeight='bold'
            ml='8px'
          >
            Associated Devices
          </DarkBox>
        </DarkFlex>
        <DarkFlex direction='row'>
          <DarkFlex direction='row' flex='1'>
            <DarkDeviceBox
              deviceName={associateDeviceValues?.doorName}
              primaryTextColor={primaryTextColor}
              secondaryTextColor={secondaryTextColor}
              title='Access Control Device'
            />
            <DarkDeviceBox
              deviceName={associateDeviceValues?.cameraName}
              primaryTextColor={primaryTextColor}
              secondaryTextColor={secondaryTextColor}
              title='Camera'
            />
            <DarkBox alignSelf='center'>
              <GridToolTip colorScheme={colorScheme} />
            </DarkBox>
          </DarkFlex>
          <DarkFlex alignItems='flex-end' gap='8px' justifyContent='flex-end'>
            <DarkTooltipTarget
              colorScheme={colorScheme}
              label="Refresh image from camera's live feed"
            >
              <DarkButton
                colorScheme={colorScheme}
                isLoading={isRefreshingImage}
                onClick={onRefreshCamera}
                size='sm'
                variant='tertiary'
              >
                {isRefreshingImage ? 'Refreshing...' : 'Refresh Camera'}
              </DarkButton>
            </DarkTooltipTarget>
            {isGridConfigured && (
              <DarkTooltipTarget
                colorScheme={colorScheme}
                label='Revert previous grids'
              >
                <DarkButton
                  colorScheme={colorScheme}
                  disabled={!shouldEnableConfigureTailgateGrid}
                  onClick={onRevertChanges}
                  size='sm'
                  variant='tertiary'
                >
                  Revert Changes
                </DarkButton>
              </DarkTooltipTarget>
            )}
            <DarkTooltipTarget
              colorScheme={colorScheme}
              label='Remove grids to unset or start again'
            >
              <DarkButton
                colorScheme={colorScheme}
                disabled={!shouldEnableConfigureTailgateGrid}
                onClick={onClearGrid}
                size='sm'
                variant='tertiary'
              >
                Clear Grids
              </DarkButton>
            </DarkTooltipTarget>
          </DarkFlex>
        </DarkFlex>
      </DarkFlex>
    )
  }

  return (
    <Flex direction='column' mb={5}>
      <Flex alignItems='center' direction='row'>
        <Icon as={AssociatedDevices} boxSize='14px' strokeColor='#4DBD6B' />
        <Box color={primaryTextColor} fontSize='12px' fontWeight='bold' ml={2}>
          Associated Devices
        </Box>
      </Flex>
      <Flex direction='row'>
        <Flex direction='row' flex={1}>
          <DeviceBox
            deviceName={associateDeviceValues?.doorName}
            primaryTextColor={primaryTextColor}
            secondaryTextColor={secondaryTextColor}
            title='Access Control Device'
          />
          <DeviceBox
            deviceName={associateDeviceValues?.cameraName}
            primaryTextColor={primaryTextColor}
            secondaryTextColor={secondaryTextColor}
            title='Camera'
          />
          <Box alignSelf='center' justifySelf='center'>
            <GridToolTip />
          </Box>
        </Flex>
        <Flex alignItems='flex-end' justifyContent='flex-end'>
          <Tooltip
            label="Refresh image from camera's live feed"
            openDelay={500}
            shouldWrapChildren
          >
            <Button
              fontSize='12px'
              isLoading={isRefreshingImage}
              loadingText='Refreshing...'
              mr='10px'
              onClick={onRefreshCamera}
              size='sm'
              variant='ghost'
            >
              Refresh Camera
            </Button>
          </Tooltip>
          {isGridConfigured && (
            <Tooltip
              label='Revert previous grids'
              openDelay={500}
              shouldWrapChildren
            >
              <Button
                fontSize='12px'
                isDisabled={!shouldEnableConfigureTailgateGrid}
                mr={2}
                onClick={onRevertChanges}
                size='sm'
                variant='ghost'
              >
                Revert Changes
              </Button>
            </Tooltip>
          )}
          <Tooltip
            label='Remove grids to unset or start again'
            openDelay={500}
            shouldWrapChildren
          >
            <Button
              fontSize='12px'
              isDisabled={!shouldEnableConfigureTailgateGrid}
              mr={2}
              onClick={onClearGrid}
              size='sm'
              variant='ghost'
            >
              Clear Grids
            </Button>
          </Tooltip>
        </Flex>
      </Flex>
    </Flex>
  )
}

interface DeviceBoxIProps {
  title: string
  deviceName: string
  primaryTextColor: string
  secondaryTextColor: string
}

const DeviceBox = ({
  title,
  deviceName,
  primaryTextColor,
  secondaryTextColor,
}: DeviceBoxIProps) => (
  <Box mr={6} mt={3}>
    <Box color={secondaryTextColor} fontSize='12px' fontWeight='medium'>
      {title}
    </Box>
    <Box color={primaryTextColor} fontSize='12px' fontWeight='medium'>
      {deviceName}
    </Box>
  </Box>
)

const DarkDeviceBox = ({
  title,
  deviceName,
  primaryTextColor,
  secondaryTextColor,
}: DeviceBoxIProps) => (
  <DarkBox mr='24px' mt='12px'>
    <DarkBox color={secondaryTextColor} fontSize='12px' fontWeight='medium'>
      {title}
    </DarkBox>
    <DarkBox color={primaryTextColor} fontSize='12px' fontWeight='medium'>
      {deviceName}
    </DarkBox>
  </DarkBox>
)
